import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { authApi, type AuthUser } from '@/lib/api';

/**
 * Decora el user con `isPlatformAdmin` derivado de `primary_role_slug`.
 *
 * justHi backend NO expone isPlatformAdmin (super_admin es un rol mas, no
 * un flag aparte). El frontend, heredado de fleethub, hace gates en muchos
 * sitios contra `user.isPlatformAdmin`. Esta funcion centraliza la
 * derivacion para que toda la app vea el flag sin tocar la API backend.
 */
function decorateUser(user: AuthUser): AuthUser {
  return {
    ...user,
    isPlatformAdmin: user.primary_role_slug === 'super_admin',
  };
}

// Helpers para leer/escribir tokens en local/session storage.
export function getStoredToken(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

export function clearStoredTokens() {
  if (typeof window === 'undefined') return;
  ['access_token', 'refresh_token'].forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

const IMPERSONATE_BACKUP_KEY = 'impersonate_backup';

/// Flag module-level (NO persistido) que evita que `checkAuth()` corra
/// más de una vez por carga del bundle.
let authCheckedThisSession = false;
function resetAuthCheck() {
  authCheckedThisSession = false;
}

export function forceRecheckAuth() {
  authCheckedThisSession = false;
}

function clearPersistedClientState() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('justhi-query-cache');
    window.localStorage.removeItem('justhi-auth');
  } catch {
    // localStorage no disponible — nada que limpiar.
  }
}

export function getImpersonateBackup(): {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
} | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(IMPERSONATE_BACKUP_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  avatarVersion: number;
  /** Paso 1 del login admin: email + password → otp_token. */
  loginRequestOtp: (email: string, password: string) => Promise<string>;
  /** Paso 2 del login admin: otp_token + code → tokens + user. */
  verifyOtp: (otpToken: string, code: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: AuthUser) => void;
  bumpAvatarVersion: () => void;
  /** Impersonation no implementada en justHi V1. DashboardLayout llama a
   *  esto sólo si `getImpersonateBackup()` devuelve algo, y como nunca se
   *  inicia, el callsite es inalcanzable en runtime. Lo declaramos para
   *  que TS valide y dejamos no-op. */
  stopImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      avatarVersion: 0,

      loginRequestOtp: async (email: string, password: string) => {
        const response = await authApi.adminLogin({ email, password });
        return response.otp_token;
      },

      verifyOtp: async (otpToken: string, code: string, rememberMe: boolean) => {
        const tokens = await authApi.verify({ otp_token: otpToken, code });
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('access_token', tokens.access_token);
        storage.setItem('refresh_token', tokens.refresh_token);
        resetAuthCheck();
        const user = decorateUser(await authApi.me());
        set({ user, isAuthenticated: true, isLoading: false });
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          // Best-effort; el backend puede haber tirado el token ya.
        }
        clearStoredTokens();
        resetAuthCheck();
        clearPersistedClientState();
        set({ user: null, isAuthenticated: false, isLoading: false });
      },

      setUser: (user: AuthUser) => set({ user: decorateUser(user) }),

      bumpAvatarVersion: () => set((state) => ({ avatarVersion: state.avatarVersion + 1 })),

      stopImpersonation: () => {
        // No-op: ver comentario en AuthState. Si en V2 implementamos
        // impersonation aquí va el flujo de restaurar tokens del backup.
      },

      checkAuth: async () => {
        if (authCheckedThisSession) return;
        authCheckedThisSession = true;

        const token = getStoredToken('access_token');
        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const user = decorateUser(await authApi.me());
          set({ user, isAuthenticated: true, isLoading: false });
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 401 || status === 403) {
            clearStoredTokens();
            set({ user: null, isAuthenticated: false, isLoading: false });
          } else {
            // Red/5xx: mantenemos `isAuthenticated: true` (offline-tolerant).
            set({ isLoading: false, isAuthenticated: true });
          }
        }
      },
    }),
    {
      name: 'justhi-auth',
      storage: createJSONStorage(() =>
        typeof window === 'undefined'
          ? (undefined as unknown as Storage)
          : window.localStorage,
      ),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          // Re-decoramos el user al rehidratar — snapshots viejos en
          // localStorage pueden no tener isPlatformAdmin computado.
          state.user = decorateUser(state.user);
          state.isLoading = false;
        }
      },
    },
  ),
);
